import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DayPickerComponent } from '../day-picker/day-picker.component';
import { TimePickerComponent } from '../time-picker/time-picker.component';
import { AppendTestIdPipe } from '../../../../../../pipes/append-test-id/append-test-id.pipe';
import { ButtonComponent } from '../../../../../feedback/button/button.component';
import { CorporateDesignService } from '../../../../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../../../../services/api/text/web-text-store.service';
import { FilterTextsPipe } from '../../../../../../pipes/filterTexts/filterTexts.pipe';
import { EEntityType, ERelationType } from '@web/dtos';
import { LanguagePipe } from '../../../../../../pipes/language/language.pipe';
import { NgStyle } from '@angular/common';
import { SiteContextService } from '../../../../../../services/site-context/site-context.service';

@Component({
  selector: 'aw-calendar',
  imports: [
    MonthPickerComponent,
    DayPickerComponent,
    TimePickerComponent,
    AppendTestIdPipe,
    ButtonComponent,
    FilterTextsPipe,
    LanguagePipe,
    NgStyle,
  ],
  templateUrl: './calendar.component.html',
})
export class CalendarComponent implements OnInit {
  public readonly date: InputSignal<Date | undefined> = input();
  public readonly id: InputSignal<string> = input.required<string>();
  public readonly parentId: InputSignal<string> = input.required<string>();
  public readonly label: InputSignal<string | undefined> = input<
    string | undefined
  >();
  public readonly showTimePicker: InputSignal<boolean> = input(true);
  public readonly borderRounding: InputSignal<string> = input('');
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly restrictedDateList: InputSignal<Date[] | null> = input<
    Date[] | null
  >(null);

  public readonly onDateConfirm: OutputEmitterRef<Date> = output<Date>();
  public readonly onDateChange: OutputEmitterRef<Date> = output<Date>();
  public readonly onCloseCalendar: OutputEmitterRef<boolean> =
    output<boolean>();

  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly focusColor: string =
    this.siteContextService.isShopContext()
      ? this.corporateDesignService.primaryColor()
      : '';

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  public innerDate: Date = new Date();

  public readonly shownMonth: WritableSignal<number> = signal(
    new Date().getMonth() + 1
  );
  protected readonly shownYear: WritableSignal<number> = signal(
    new Date().getFullYear()
  );

  protected readonly calendarLabel: Signal<string> = computed((): string => {
    return this.label() ?? 'Reisedatum';
  });

  protected textColor: Signal<string> = computed(() => {
    return this.corporateDesignService.isDarkModeActive()
      ? this.corporateDesignService.corporateDesign().colorAdmixtures.dark_text
      : '';
  });

  constructor() {
    this.innerDate.setSeconds(0);
    this.shownMonth.set(this.innerDate.getMonth() + 1);
    this.shownYear.set(this.innerDate.getFullYear());
    effect(() => {
      this.reset(this.date());
    });
  }

  ngOnInit(): void {
    this.shownMonth.set(
      this.date() ? this.date()!.getMonth() + 1 : new Date().getMonth() + 1
    );
    this.shownYear.set(
      this.date() ? this.date()!.getFullYear() : new Date().getFullYear()
    );
    this.reset(this.date());
  }

  public reset(date: Date | undefined) {
    this.innerDate = date || new Date();
    if (!this.showTimePicker()) {
      this.innerDate.setMinutes(0);
      this.innerDate.setHours(0);
      this.innerDate.setSeconds(0);
    }
    this.innerDate = new Date(this.innerDate);
  }

  protected setDate(day: number) {
    this.innerDate = new Date(
      `${this.shownMonth()}.${day}.${this.shownYear()} ${this.innerDate.getHours()}:${this.innerDate.getMinutes()}`
    );
    this.onDateChange.emit(this.innerDate);
  }

  protected confirm(): void {
    this.setDate(this.innerDate.getDate());
    this.onDateConfirm.emit(this.innerDate);
  }

  protected setMinutes(minutes: string) {
    this.innerDate.setMinutes(+minutes);
    this.innerDate = new Date(this.innerDate);
  }

  protected setHours(hours: string) {
    this.innerDate.setHours(+hours);
    this.innerDate = new Date(this.innerDate);
  }
}
